import styled, { css } from 'styled-components';
import { theme } from './theme';

// Button variations
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'link';
type ButtonSize = 'small' | 'medium' | 'large';

const getButtonStyles = (variant: ButtonVariant = 'primary') => {
  switch (variant) {
    case 'primary':
      return css`
        background: ${theme.colors.gradient.gold};
        color: ${theme.colors.secondary};
        border: none;
        box-shadow: ${theme.shadows.gold};
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: ${theme.shadows.gold}, ${theme.shadows.glow};
        }
        
        &:active {
          transform: translateY(1px);
        }
      `;
    case 'secondary':
      return css`
        background: ${theme.colors.gradient.dark};
        color: ${theme.colors.background};
        border: none;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: ${theme.shadows.medium};
        }
        
        &:active {
          transform: translateY(1px);
        }
      `;
    case 'outline':
      return css`
        background-color: transparent;
        color: ${theme.colors.secondary};
        border: 2px solid ${theme.colors.secondary};
        
        &:hover {
          background-color: ${theme.colors.secondary};
          color: ${theme.colors.primary};
          transform: translateY(-2px);
        }
        
        &:active {
          transform: translateY(1px);
        }
      `;
    case 'ghost':
      return css`
        background-color: transparent;
        color: ${theme.colors.secondary};
        border: none;
        
        &:hover {
          background-color: ${theme.colors.lightGray};
          transform: translateY(-2px);
        }
        
        &:active {
          transform: translateY(1px);
        }
      `;
    case 'link':
      return css`
        background-color: transparent;
        color: ${theme.colors.primary};
        border: none;
        padding: 0;
        height: auto;
        font-weight: 500;
        text-decoration: underline;
        
        &:hover {
          color: ${theme.colors.primaryDark};
          text-decoration: none;
        }
      `;
    default:
      return '';
  }
};

const getButtonSize = (size: ButtonSize = 'medium') => {
  switch (size) {
    case 'small':
      return css`
        padding: ${theme.spacing.xs} ${theme.spacing.md};
        font-size: 0.875rem;
        height: 36px;
      `;
    case 'medium':
      return css`
        padding: ${theme.spacing.sm} ${theme.spacing.lg};
        font-size: 1rem;
        height: 44px;
      `;
    case 'large':
      return css`
        padding: ${theme.spacing.md} ${theme.spacing.xl};
        font-size: 1.125rem;
        height: 52px;
      `;
    default:
      return '';
  }
};

export const Button = styled.button<{ 
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
}>`
  ${({ variant }) => getButtonStyles(variant)};
  ${({ size }) => getButtonSize(size)};
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  border-radius: ${theme.borderRadius.pill};
  font-weight: 600;
  transition: ${theme.transitions.medium};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  letter-spacing: 0.03em;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    pointer-events: none;
    background-image: radial-gradient(circle, #fff 10%, transparent 10.01%);
    background-repeat: no-repeat;
    background-position: 50%;
    transform: scale(10, 10);
    opacity: 0;
    transition: transform .3s, opacity .5s;
  }
  
  &:active:after {
    transform: scale(0, 0);
    opacity: .3;
    transition: 0s;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
`;

export const Card = styled.div<{
  elevation?: 'low' | 'medium' | 'high';
  hoverable?: boolean;
}>`
  background-color: ${theme.colors.background};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${({ elevation }) => 
    elevation === 'high' 
      ? theme.shadows.large 
      : elevation === 'medium'
        ? theme.shadows.medium
        : theme.shadows.small};
  padding: ${theme.spacing.lg};
  transition: ${theme.transitions.medium};
  overflow: hidden;
  position: relative;
  height: 100%;
  
  ${({ hoverable }) => hoverable && css`
    &:hover {
      transform: translateY(-5px);
      box-shadow: ${theme.shadows.large};
    }
  `}
`;

export const Input = styled.input<{ error?: boolean }>`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 2px solid ${({ error }) => 
    error ? theme.colors.error : theme.colors.gray};
  border-radius: ${theme.borderRadius.medium};
  font-size: 1rem;
  transition: ${theme.transitions.short};
  background-color: ${theme.colors.background};
  
  &:focus {
    outline: none;
    border-color: ${({ error }) => 
      error ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ error }) => 
      error 
        ? 'rgba(255, 0, 0, 0.1)' 
        : 'rgba(255, 215, 0, 0.1)'};
  }
  
  &::placeholder {
    color: ${theme.colors.gray};
  }
  
  &.error {
    border-color: ${theme.colors.error};
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.1);
    }
  }
`;

export const Select = styled.select<{ error?: boolean }>`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 2px solid ${({ error }) => 
    error ? theme.colors.error : theme.colors.gray};
  border-radius: ${theme.borderRadius.medium};
  font-size: 1rem;
  background-color: ${theme.colors.background};
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23333333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right ${theme.spacing.md} center;
  background-size: 16px;
  padding-right: 40px;
  
  &:focus {
    outline: none;
    border-color: ${({ error }) => 
      error ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ error }) => 
      error 
        ? 'rgba(255, 0, 0, 0.1)' 
        : 'rgba(255, 215, 0, 0.1)'};
  }
  
  &.error {
    border-color: ${theme.colors.error};
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.1);
    }
  }
`;

export const TextArea = styled.textarea<{ error?: boolean }>`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: 2px solid ${({ error }) => 
    error ? theme.colors.error : theme.colors.gray};
  border-radius: ${theme.borderRadius.medium};
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  background-color: ${theme.colors.background};
  
  &:focus {
    outline: none;
    border-color: ${({ error }) => 
      error ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ error }) => 
      error 
        ? 'rgba(255, 0, 0, 0.1)' 
        : 'rgba(255, 215, 0, 0.1)'};
  }
  
  &::placeholder {
    color: ${theme.colors.gray};
  }
  
  &.error {
    border-color: ${theme.colors.error};
    
    &:focus {
      box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.1);
    }
  }
`;

export const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.md};
  
  label {
    display: block;
    margin-bottom: ${theme.spacing.xs};
    font-weight: 500;
    font-size: 0.9rem;
    color: ${theme.colors.textLight};
  }
  
  .error-message {
    color: ${theme.colors.error};
    font-size: 0.875rem;
    margin-top: ${theme.spacing.xs};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xxs};
    
    &:before {
      content: '!';
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      background-color: ${theme.colors.error};
      color: white;
      border-radius: 50%;
      font-size: 12px;
      font-weight: bold;
    }
  }
`;

export const Grid = styled.div<{ 
  columns?: number; 
  gap?: string; 
  mobileColumns?: number;
  tabletColumns?: number; 
}>`
  display: grid;
  grid-template-columns: repeat(${({ mobileColumns }) => mobileColumns || 1}, 1fr);
  gap: ${({ gap }) => gap || theme.spacing.md};
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(${({ tabletColumns, columns }) => 
      tabletColumns || Math.min(columns || 2, 2)}, 1fr);
  }
  
  @media (min-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(${({ columns }) => columns || 3}, 1fr);
  }
`;

export const Flex = styled.div<{ 
  direction?: string; 
  justify?: string; 
  align?: string;
  gap?: string;
  wrap?: string;
  responsive?: boolean;
}>`
  display: flex;
  flex-direction: ${({ direction, responsive }) => 
    responsive ? 'column' : direction || 'row'};
  justify-content: ${({ justify }) => justify || 'flex-start'};
  align-items: ${({ align }) => align || 'stretch'};
  gap: ${({ gap }) => gap || theme.spacing.md};
  flex-wrap: ${({ wrap }) => wrap || 'nowrap'};
  
  @media (min-width: ${theme.breakpoints.tablet}) {
    flex-direction: ${({ direction }) => direction || 'row'};
  }
`;

export const Badge = styled.span<{ 
  type?: 'success' | 'error' | 'info' | 'warning' | 'primary';
  size?: 'small' | 'medium' | 'large';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xxs};
  padding: ${({ size }) => 
    size === 'large' 
      ? `${theme.spacing.xs} ${theme.spacing.md}` 
      : size === 'small'
        ? `${theme.spacing.xxs} ${theme.spacing.xs}`
        : `${theme.spacing.xs} ${theme.spacing.sm}`};
  border-radius: ${theme.borderRadius.pill};
  font-size: ${({ size }) => 
    size === 'large' ? '0.875rem' : size === 'small' ? '0.7rem' : '0.75rem'};
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  
  ${({ type = 'primary' }) => {
    switch (type) {
      case 'success':
        return css`
          background-color: rgba(40, 167, 69, 0.15);
          color: #155724;
        `;
      case 'error':
        return css`
          background-color: rgba(255, 0, 0, 0.15);
          color: #721c24;
        `;
      case 'warning':
        return css`
          background-color: rgba(255, 193, 7, 0.15);
          color: #856404;
        `;
      case 'info':
        return css`
          background-color: rgba(23, 162, 184, 0.15);
          color: #0c5460;
        `;
      case 'primary':
      default:
        return css`
          background-color: rgba(255, 215, 0, 0.15);
          color: ${theme.colors.secondary};
        `;
    }
  }}
`;

export const Container = styled.div<{ maxWidth?: string }>`
  width: 100%;
  max-width: ${({ maxWidth }) => maxWidth || '1280px'};
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};
  
  @media (min-width: ${theme.breakpoints.desktop}) {
    padding: 0 ${theme.spacing.lg};
  }
`;

export const Section = styled.section<{ 
  padding?: string;
  background?: string; 
}>`
  padding: ${({ padding }) => padding || `${theme.spacing.xl} 0`};
  background-color: ${({ background }) => background || 'transparent'};
  position: relative;
  overflow: hidden;
  
  @media (min-width: ${theme.breakpoints.desktop}) {
    padding: ${({ padding }) => padding || `${theme.spacing.xxl} 0`};
  }
`;

export const Divider = styled.hr<{ 
  color?: string; 
  vertical?: boolean;
  spacing?: string;
  width?: string;
}>`
  border: none;
  border-${({ vertical }) => vertical ? 'left' : 'top'}: 1px solid ${({ color }) => 
    color || theme.colors.gray};
  margin: ${({ spacing, vertical }) => 
    vertical 
      ? `0 ${spacing || theme.spacing.md}` 
      : `${spacing || theme.spacing.md} 0`};
  width: ${({ width, vertical }) => vertical ? 'auto' : (width || '100%')};
  height: ${({ vertical }) => vertical ? 'auto' : 'auto'};
  align-self: ${({ vertical }) => vertical ? 'stretch' : 'auto'};
`;

export const Tooltip = styled.div<{ position?: string }>`
  position: relative;
  display: inline-block;
  
  &:hover .tooltip-text {
    visibility: visible;
    opacity: 1;
  }
  
  .tooltip-text {
    visibility: hidden;
    width: auto;
    min-width: 120px;
    max-width: 250px;
    background-color: ${theme.colors.secondary};
    color: ${theme.colors.background};
    text-align: center;
    border-radius: ${theme.borderRadius.small};
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    position: absolute;
    z-index: ${theme.zIndex.tooltip};
    opacity: 0;
    transition: opacity 0.3s;
    font-size: 0.875rem;
    white-space: nowrap;
    box-shadow: ${theme.shadows.medium};
    
    ${({ position = 'top' }) => {
      switch (position) {
        case 'top':
          return css`
            bottom: 125%;
            left: 50%;
            transform: translateX(-50%);
            
            &:after {
              content: "";
              position: absolute;
              top: 100%;
              left: 50%;
              margin-left: -5px;
              border-width: 5px;
              border-style: solid;
              border-color: ${theme.colors.secondary} transparent transparent transparent;
            }
          `;
        case 'bottom':
          return css`
            top: 125%;
            left: 50%;
            transform: translateX(-50%);
            
            &:after {
              content: "";
              position: absolute;
              bottom: 100%;
              left: 50%;
              margin-left: -5px;
              border-width: 5px;
              border-style: solid;
              border-color: transparent transparent ${theme.colors.secondary} transparent;
            }
          `;
        case 'left':
          return css`
            top: 50%;
            right: 125%;
            transform: translateY(-50%);
            
            &:after {
              content: "";
              position: absolute;
              top: 50%;
              left: 100%;
              margin-top: -5px;
              border-width: 5px;
              border-style: solid;
              border-color: transparent transparent transparent ${theme.colors.secondary};
            }
          `;
        case 'right':
          return css`
            top: 50%;
            left: 125%;
            transform: translateY(-50%);
            
            &:after {
              content: "";
              position: absolute;
              top: 50%;
              right: 100%;
              margin-top: -5px;
              border-width: 5px;
              border-style: solid;
              border-color: transparent ${theme.colors.secondary} transparent transparent;
            }
          `;
        default:
          return '';
      }
    }}
  }
`; 